/*****************************************************
Naam: CustomerGegevensPanel.jsx
Locatie: src/components/CustomerPortal/CustomerGegevensPanel.jsx
Datum: 2026-01-25
Versie: 1.0.0
Auteur: Claude
******************************************************
BESCHRIJVING:
Panel voor goederen invoer en services selectie in het klantenportal.
Gebaseerd op dashboard GegevensPanel maar aangepast voor klanten.

FEATURES:
- 5 rijen goederen invoer
- Soort dropdown met auto-fill afmetingen voor pallets
- Stackable selectie (J/N)
- Afmetingen (L x B x H in meters)
- Gewicht (kg)
- 11 services checkboxes
- Zelfde styling als dashboard

Props:
- goederen: Array met 5 goederen objecten
- services: Object met service booleans
- onGoederenChange: (rowIndex, field, value) => void
- onServiceChange: (serviceName, value) => void
******************************************************/

import React from 'react';
import { Package, Truck } from 'lucide-react';

const CustomerGegevensPanel = ({ 
  goederen, 
  services, 
  onGoederenChange, 
  onServiceChange 
}) => {
  // Soort opties voor dropdown
  const soortOpties = [
    'BIGBAG', 'BUNDEL', 'CONTAINER', 'CTNS', 'FCL',
    'FTL', 'HPLTS', 'KIST', 'Colli', 'PLTS', 'EuroPLTS', 'BlockPLTS'
  ];

  // Services configuratie met iconen en beschrijvingen
  const servicesConfig = [
    { key: 'serviceExpress', label: 'Express', description: 'Spoedzending' },
    { key: 'serviceParcel', label: 'Pakket', description: 'Kleine zending' },
    { key: 'serviceGroupage', label: 'Groupage', description: 'Deellading' },
    { key: 'serviceLTL', label: 'LTL', description: 'Less than truckload' },
    { key: 'serviceFTL', label: 'FTL', description: 'Full truckload' },
    { key: 'serviceCustoms', label: 'Douane', description: 'Douaneafhandeling' },
    { key: 'serviceKooiaap', label: 'Kooiaap', description: 'Met kooiaap' },
    { key: 'serviceKlep', label: 'Klep', description: 'Laadklep nodig' },
    { key: 'serviceLastMile', label: 'Last Mile', description: 'Tot de deur' },
    { key: 'serviceADR', label: 'ADR', description: 'Gevaarlijke stoffen' },
    { key: 'serviceGeconditioneerd', label: 'Gecond.', description: 'Temperatuur' }
  ];

  // Handler voor soort selectie met auto-fill afmetingen
  const handleSoortChange = (row, value) => {
    onGoederenChange(row, 'soort', value);
    
    // Auto-fill afmetingen voor pallet types
    if (value === 'EuroPLTS') {
      onGoederenChange(row, 'lengte', '1.20');
      onGoederenChange(row, 'breedte', '0.80');
    } else if (value === 'BlockPLTS') {
      onGoederenChange(row, 'lengte', '1.20');
      onGoederenChange(row, 'breedte', '1.00');
    }
  };

  return (
    <div className="border-2 border-gray-300 p-5 bg-gray-50">
      <h3 className="font-bold text-lg mb-4 text-gray-800 flex items-center gap-2">
        <Package className="w-5 h-5 text-blue-900" />
        Goederen & Services
      </h3>
      
      {/* ========== GOEDEREN TABEL ========== */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden mb-5">
        <div className="overflow-x-auto">
          <table className="w-full border-collapse min-w-[800px]">
            <thead>
              <tr className="bg-gray-100">
                <th className="border-b border-gray-200 px-3 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider w-20">
                  Aantal
                </th>
                <th className="border-b border-gray-200 px-3 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider w-28">
                  Soort
                </th>
                <th className="border-b border-gray-200 px-3 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Omschrijving
                </th>
                <th className="border-b border-gray-200 px-3 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider w-20">
                  Stack
                </th>
                <th className="border-b border-gray-200 px-3 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider w-20">
                  L (m)
                </th>
                <th className="border-b border-gray-200 px-3 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider w-20">
                  B (m)
                </th>
                <th className="border-b border-gray-200 px-3 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider w-20">
                  H (m)
                </th>
                <th className="border-b border-gray-200 px-3 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider w-24">
                  Gewicht
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {goederen.map((goed, row) => (
                <tr key={row} className={row % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                  <td className="px-2 py-2">
                    <input
                      type="number"
                      min="0"
                      value={goed.aantal}
                      onChange={(e) => onGoederenChange(row, 'aantal', e.target.value)}
                      placeholder="0"
                      className="w-full px-2 py-2 text-sm border border-gray-200 rounded-none focus:ring-2 focus:ring-blue-300 focus:border-transparent transition-all"
                    />
                  </td>
                  <td className="px-2 py-2">
                    <select 
                      value={goed.soort}
                      onChange={(e) => handleSoortChange(row, e.target.value)}
                      className="w-full px-2 py-2 text-sm border border-gray-200 rounded-none focus:ring-2 focus:ring-blue-300 focus:border-transparent transition-all bg-white"
                    >
                      <option value="">--</option>
                      {soortOpties.map((soort, idx) => (
                        <option key={idx} value={soort}>{soort}</option>
                      ))}
                    </select>
                  </td>
                  <td className="px-2 py-2">
                    <input
                      type="text"
                      value={goed.omschrijving}
                      onChange={(e) => onGoederenChange(row, 'omschrijving', e.target.value)}
                      placeholder="Beschrijving goederen..."
                      className="w-full px-2 py-2 text-sm border border-gray-200 rounded-none focus:ring-2 focus:ring-blue-300 focus:border-transparent transition-all"
                    />
                  </td>
                  <td className="px-2 py-2">
                    <select 
                      value={goed.stackable}
                      onChange={(e) => onGoederenChange(row, 'stackable', e.target.value)}
                      className="w-full px-2 py-2 text-sm border border-gray-200 rounded-none focus:ring-2 focus:ring-blue-300 focus:border-transparent transition-all bg-white"
                    >
                      <option value="--">--</option>
                      <option value="J">Ja</option>
                      <option value="N">Nee</option>
                    </select>
                  </td>
                  <td className="px-2 py-2">
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={goed.lengte}
                      onChange={(e) => onGoederenChange(row, 'lengte', e.target.value)}
                      placeholder="0.00"
                      className={`w-full px-2 py-2 text-sm border rounded-none focus:ring-2 focus:ring-blue-300 focus:border-transparent transition-all ${(goed.soort || goed.omschrijving || goed.aantal) && !goed.lengte ? 'border-orange-400 bg-orange-50' : 'border-gray-200'}`}
                    />
                  </td>
                  <td className="px-2 py-2">
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={goed.breedte}
                      onChange={(e) => onGoederenChange(row, 'breedte', e.target.value)}
                      placeholder="0.00"
                      className={`w-full px-2 py-2 text-sm border rounded-none focus:ring-2 focus:ring-blue-300 focus:border-transparent transition-all ${(goed.soort || goed.omschrijving || goed.aantal) && !goed.breedte ? 'border-orange-400 bg-orange-50' : 'border-gray-200'}`}
                    />
                  </td>
                  <td className="px-2 py-2">
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={goed.hoogte}
                      onChange={(e) => onGoederenChange(row, 'hoogte', e.target.value)}
                      placeholder="0.00"
                      className={`w-full px-2 py-2 text-sm border rounded-none focus:ring-2 focus:ring-blue-300 focus:border-transparent transition-all ${(goed.soort || goed.omschrijving || goed.aantal) && !goed.hoogte ? 'border-orange-400 bg-orange-50' : 'border-gray-200'}`}
                    />
                  </td>
                  <td className="px-2 py-2">
                    <div className="relative">
                      <input
                        type="number"
                        step="0.1"
                        min="0"
                        value={goed.gewicht}
                        onChange={(e) => onGoederenChange(row, 'gewicht', e.target.value)}
                        placeholder="0"
                        className="w-full px-2 py-2 pr-8 text-sm border border-gray-200 rounded-none focus:ring-2 focus:ring-blue-300 focus:border-transparent transition-all"
                      />
                      <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-gray-400">kg</span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {/* Tip voor afmetingen */}
        <div className="px-4 py-2 bg-blue-50 border-t border-blue-100 text-xs text-blue-700">
          💡 <strong>Tip:</strong> Selecteer "EuroPLTS" of "BlockPLTS" voor automatische afmetingen
        </div>
      </div>

      {/* ========== SERVICES CHECKBOXES ========== */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <h4 className="font-semibold text-sm text-gray-700 mb-3 flex items-center gap-2">
          <Truck className="w-4 h-4 text-blue-900" />
          Benodigde Services
        </h4>
        
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
          {servicesConfig.map((service) => (
            <label
              key={service.key}
              className={`
                flex items-center gap-2 p-3 rounded-lg border-2 cursor-pointer transition-all
                ${services[service.key] 
                  ? 'border-blue-300 bg-blue-50 text-blue-900' 
                  : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                }
              `}
            >
              <input
                type="checkbox"
                checked={services[service.key] || false}
                onChange={(e) => onServiceChange(service.key, e.target.checked)}
                className="w-4 h-4 text-blue-900 rounded focus:ring-blue-300"
              />
              <div className="flex flex-col">
                <span className="text-sm font-medium">{service.label}</span>
                <span className="text-xs opacity-70">{service.description}</span>
              </div>
            </label>
          ))}
        </div>
        
        {/* ADR Waarschuwing */}
        {services.serviceADR && (
          <div className="mt-3 p-3 bg-orange-50 border border-orange-200 rounded-lg text-sm text-orange-800">
            ⚠️ <strong>Let op:</strong> ADR transport vereist speciale documentatie. 
            We nemen contact met u op voor de benodigde gegevens.
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomerGegevensPanel;
